import { Box, Chip, Stack, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { useSignup } from '../SignupContext';
import { TIER_PRICING, type SignupTier, type SignupBilling } from '../types';

interface TierCardProps {
  tier: SignupTier;
  name: string;
  tagline: string;
  features: string[];
  selected: boolean;
  popular?: boolean;
  billing: SignupBilling;
  onSelect: () => void;
}

function TierCard({ tier, name, tagline, features, selected, popular, billing, onSelect }: TierCardProps) {
  const price = TIER_PRICING[tier];
  const displayPrice = billing === 'yearly' ? price.yearly : price.monthly;
  const priceLabel =
    tier === 'free'
      ? '/ sempre'
      : billing === 'yearly'
        ? '/ anno'
        : '/ mese';

  const accentColor =
    tier === 'free'
      ? '#52525B'
      : tier === 'base'
        ? '#0EA5E9'
        : tier === 'pro'
          ? '#2563EB'
          : '#7C3AED';

  return (
    <Box
      role="button"
      onClick={onSelect}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        p: { xs: 2.5, md: 3 },
        border: `2px solid ${selected ? accentColor : 'var(--dust)'}`,
        borderRadius: 1.5,
        bgcolor: 'var(--paper)',
        transition: 'all 220ms cubic-bezier(0.2, 0, 0.2, 1)',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        '&:hover': {
          borderColor: accentColor,
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
        },
      }}
    >
      {popular && (
        <Chip
          label="Più popolare"
          size="small"
          sx={{
            position: 'absolute',
            top: -10,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: accentColor,
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.65rem',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}
        />
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono Variable", monospace',
            fontSize: '0.7rem',
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: accentColor,
            fontWeight: 700,
          }}
        >
          {name}
        </Typography>
        {selected && <CheckCircleIcon sx={{ color: accentColor, fontSize: 22 }} />}
      </Box>

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography
            sx={{
              fontFamily: '"Fraunces Variable", Georgia, serif',
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 540,
              letterSpacing: '-0.03em',
              lineHeight: 1,
              color: 'var(--ink)',
            }}
          >
            €{displayPrice}
          </Typography>
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono Variable", monospace',
              fontSize: '0.8rem',
              color: 'var(--ink-soft)',
            }}
          >
            {priceLabel}
          </Typography>
        </Box>
        <Typography sx={{ mt: 0.5, fontSize: '0.85rem', color: 'var(--ink-soft)' }}>
          {tagline}
        </Typography>
      </Box>

      <Stack spacing={0.75} sx={{ flex: 1 }}>
        {features.map((f, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
            <CheckCircleIcon sx={{ color: accentColor, fontSize: 14, mt: 0.4, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '0.8rem', color: 'var(--ink)', lineHeight: 1.4 }}>
              {f}
            </Typography>
          </Box>
        ))}
      </Stack>

      {tier !== 'free' && (
        <Typography
          sx={{
            mt: 1,
            fontFamily: '"JetBrains Mono Variable", monospace',
            fontSize: '0.65rem',
            color: 'var(--ink-soft)',
            letterSpacing: '0.04em',
          }}
        >
          30 giorni gratis · no carta credito
        </Typography>
      )}
    </Box>
  );
}

export default function StepTier() {
  const { state, update } = useSignup();
  const { t } = useTranslation('signup');

  const selectedTier: SignupTier = state.tier ?? 'free';
  const billing: SignupBilling = state.billing_frequency ?? 'monthly';

  const tiers: Array<{
    key: SignupTier;
    name: string;
    tagline: string;
    features: string[];
    popular?: boolean;
  }> = [
    {
      key: 'free',
      name: 'Free',
      tagline: 'Marketplace gratuito per sempre',
      features: [
        'Iscrizione al marketplace EPC',
        'Match automatici per la tua zona',
        'Profilo azienda pubblico',
        '1 utente',
      ],
    },
    {
      key: 'base',
      name: 'Base',
      tagline: 'Il gestionale essenziale',
      features: [
        'Tutto del Free, più…',
        'Lead, preventivi, commesse',
        'Pagamenti e fatture base',
        'Fino a 5 utenti',
      ],
    },
    {
      key: 'pro',
      name: 'Pro',
      tagline: "L'automazione completa",
      popular: true,
      features: [
        'Tutto del Base, più…',
        'Lead automatici da Facebook/Google',
        'Simulazione 2D pannelli',
        'Firma remota, area cliente',
        'Fino a 20 utenti',
      ],
    },
    {
      key: 'business',
      name: 'Business',
      tagline: 'La suite completa con AI',
      features: [
        'Tutto del Pro, più…',
        'Assistente vocale AI',
        'App Cantiere Android',
        'Integrazione Reviso',
        'Utenti illimitati',
      ],
    },
  ];

  return (
    <Stack spacing={4}>
      <Box>
        <Typography
          variant="h2"
          sx={{
            fontFamily: '"Fraunces Variable", Georgia, serif',
            fontSize: { xs: '2.4rem', md: '3.2rem' },
            letterSpacing: '-0.02em',
            lineHeight: 1,
            mb: 2,
          }}
        >
          {t('step_tier.titolo', { defaultValue: 'Scegli il tuo piano' })}
        </Typography>
        <Typography sx={{ color: 'var(--ink-soft)', fontSize: '1rem' }}>
          {t('step_tier.sottotitolo', {
            defaultValue:
              'Tutti i tier a pagamento includono 30 giorni di prova senza carta di credito. Disdici quando vuoi.',
          })}
        </Typography>
      </Box>

      {/* Toggle mensile/annuale */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <ToggleButtonGroup
          value={billing}
          exclusive
          onChange={(_, val) => {
            if (val) update({ billing_frequency: val as SignupBilling });
          }}
          sx={{
            bgcolor: 'var(--bone)',
            p: 0.5,
            borderRadius: 999,
            border: '1px solid var(--dust)',
            '& .MuiToggleButton-root': {
              border: 'none',
              borderRadius: '999px !important',
              px: 2.5,
              py: 0.75,
              fontSize: '0.85rem',
              fontWeight: 600,
              fontFamily: '"Plus Jakarta Sans Variable", sans-serif',
              color: 'var(--ink-soft)',
              textTransform: 'none',
              '&.Mui-selected': {
                bgcolor: 'var(--paper)',
                color: 'var(--ink)',
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
              },
              '&:hover': { bgcolor: 'transparent' },
            },
          }}
        >
          <ToggleButton value="monthly">Mensile</ToggleButton>
          <ToggleButton value="yearly">
            Annuale
            <Box
              component="span"
              sx={{
                ml: 1,
                fontSize: '0.65rem',
                fontWeight: 700,
                color: '#10B981',
                bgcolor: 'rgba(16,185,129,0.1)',
                px: 0.75,
                py: 0.1,
                borderRadius: 999,
              }}
            >
              −17%
            </Box>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 2,
          alignItems: 'stretch',
        }}
      >
        {tiers.map((tier) => (
          <TierCard
            key={tier.key}
            tier={tier.key}
            name={tier.name}
            tagline={tier.tagline}
            features={tier.features}
            popular={tier.popular}
            selected={selectedTier === tier.key}
            billing={billing}
            onSelect={() => update({ tier: tier.key })}
          />
        ))}
      </Box>
    </Stack>
  );
}
